// #region imports
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, isDevMode, OnDestroy, OnInit, QueryList, TemplateRef, ViewChild, ViewChildren } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormGroup, AbstractControl } from '@angular/forms';

import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatExpansionPanel } from '@angular/material/expansion';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTable } from '@angular/material/table';
import { FeedbackSnackbarComponent } from 'src/app/common/feedback-snackbar/feedback-snackbar.component';
import { Clipboard } from "@angular/cdk/clipboard";

import { ApplicationScreen } from 'src/app/application-navigator/application-screen';
import { AlertDialog } from 'src/app/common/alert-dialog/alert-dialog.component';
import { IPrinter, WMSService } from 'src/app/services/wms.service';
import { AudioService } from 'src/app/services/audio.service';

import { AcumaticaService } from 'src/app/acumatica/acumatica.service';
import { NotFoundError } from 'src/app/acumatica/acumatica-errors';
import { Customer, CustomerSerivce } from 'src/app/acumatica/EntityServices/Customer.service';
import { SalesOrder, SalesOrderService } from 'src/app/acumatica/EntityServices/SalesOrder.service';
import { Shipment, ShipmentDetail, OrderPickingService, InventorySource, ShipmentDetailAllocation, CompleteShipmentStatus, CompleteShipmentStepStatus } from 'src/app/acumatica/EntityServices/OrderPicking.service';
import { StockItem, StockItemService } from 'src/app/acumatica/EntityServices/StockItem.service';
import { InventorySourceService } from './../../acumatica/EntityServices/OrderPicking.service';
import { WarehouseService } from 'src/app/acumatica/EntityServices/Warehouse.service';
import { AppConfig } from 'src/app/app-config.service';
import { Sound } from 'src/app/services/audio.service';
import { PickDetailEditorComponent } from './pick-detail-editor/pick-detail-editor.component';
import { PickCompletionDialogComponent } from './pick-completion-dialog/pick-completion-dialog.component';
import { LoadingIndicator } from 'src/app/acumatica/loading-indicator/AcumaticaLoadingOptions';
import { ConfirmationDialog } from 'src/app/common/confirmation-dialog/confirmation-dialog.component';
import { PrinterSelectDialog } from 'src/app/common/printer-selector/printer-selector.component';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { InventoryAdjustmentEditorComponent } from '../../common/inventory-adjustment-editor/inventory-adjustment-editor.component';
import { LogService } from 'src/app/services/logging.service';
import { InventoryLocationEditorComponent } from '../../common/inventory-location-editor/inventory-location-editor.component';
// #endregion

@Component({
  selector: 'app-order-picking2',
  templateUrl: './order-picking2.component.html',
  styleUrls: ['./order-picking2.component.css']
})
export class OrderPicking2Component extends ApplicationScreen implements OnInit, OnDestroy {

  // #region component-wide vars

  settings = AppConfig.settings;
  /** the Shipment object currently being worked */
  shipment!: Shipment;
  /** the Customer object for the Shipment */
  customer: Customer | null = null;
  /** an array of FormGroups for each Shipment Detail object indexed within the array by the ShipmentDetail.LineNbr */
  pickFormGroups: FormGroup[] = [];
  noop = () => { };
  /** indicator for Inventory table to display Hash Codes or full Lot Numbers */
  displayHashCodes = false;
  /** non-stock items removed from Shipment Details when shipment loading, merged back in when shipment updated */
  nonStockItemHold: ShipmentDetail[] = [];

  /** encapsulation object for interating with the current Pick Entry form */
  current: ICurrentEntryForm = {
    detail: null,
    item: null,
    source: null,
    picksTable: null,
    formGroup: () => this.pickFormGroups[this.current.detail!.LineNbr!],
    getControl: (path: string) => new Promise<AbstractControl>(resolve => {
      // form group isn't created until the inventory data is available
      let self = this;
      let inventoryID = this.current.detail!.InventoryID;
      let waitForControl = () => {
        if (!this.current.detail)
          setTimeout(waitForControl, 200)
        else if (self.inventory[inventoryID] === undefined)
          setTimeout(waitForControl, 200);
        else
          resolve(this.current.formGroup().get(path)!);
      };
      setTimeout(waitForControl);
    }),
    focusFirstInput: () => {
      if (this.current.detail != null)
        this.current.detail!.isLotSerItem ? this.current.LotSerNbr.focus() : this.current.Qty.focus()
    },
    resetForm: () => {
      this.current.detail!.QuickAction = null;
      this.current.detail!.forceIncomplete = false;
      this.current.source = null;
      this.current.LotSerNbr.reset();
      this.current.Qty.reset();
      this.current.focusFirstInput();
    },

    refreshPicksTable: () => { this.current.picksTable?.renderRows(); },

    createAllocation: () => {
      let alloc = this.current.detail!.NewAllocation(this.current.source!);
      alloc.Qty = this.current.Qty.value() || 0;
      this.current.refreshPicksTable();
      return alloc;
    },

    LotSerNbr: {
      control: () => this.current.getControl('lotSerNbr')!,
      focus: () => this.current.LotSerNbr.control().then(ctl =>
        // if an underlying Input doesn't exist for the FormField ctl is an object but .focus won't exist
        (<any>ctl).focus?.call()
      ),
      value: (): string => this.current.formGroup().get('lotSerNbr')!.value,
      reset: () => {
        this.current.Qty.reset();
        this.current.formGroup().get('lotSerNbr')!.setValue(null);
        this.current.source = null;
        this.current.detail!.QuickAction = null;
        return this.current.LotSerNbr.focus();
      }
    },
    Qty: {
      control: () => this.current.getControl('qty')!,
      focus: () => this.current.Qty.control().then(ctl => (<any>ctl).focus()),
      value: (): number | null => this.current.formGroup().get('qty')!.value || null,
      reset: () => {
        this.current.formGroup().get('qty')!.setValue(null);
        return this.current.Qty.focus();
      }
    }
  };

  get showCompletedLines(): boolean { return localStorage.getItem('picking-show-completed-lines') == 'YES' || false; }
  set showCompletedLines(value: boolean) { localStorage.setItem('picking-show-completed-lines', value ? 'YES' : 'NO'); }

  // #endregion

  // #region HTML Template UI exposure & element references

  // panel containing [Complete] button displayed when picking is complete - ElementRef to scroll into view at completion of picking
  @ViewChild('pickingCompletePanel') pickingCompletePanel?: ElementRef;
  scrollToTop() { (<any>this.pickingCompletePanel!)._elementRef.nativeElement.scrollIntoView({ behavior: "smooth", block: "nearest" }); }

  isDevMode = isDevMode();
  inventoryDisplayColumns = ['LocationID', 'LotSerialNbr', 'CutCount', 'QtyAvailable', 'menu'];
  allocationsDisplayColumns = ['LocationID', 'LotSerialNbr', 'Qty', 'Flag', 'menu'];
  CompleteShipmentStepStatus = CompleteShipmentStepStatus;

  // #endregion

  // #region Constructor, ApplicationScreen
  pageTitle = new FormControl("Picking");

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private changeDetector: ChangeDetectorRef,
    private clipboard: Clipboard,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private sounds: AudioService,
    private errorHandler: ErrorHandlerService,
    private acumatica: AcumaticaService,
    private logger: LogService,
    private wms: WMSService,
    private warehouses: WarehouseService,
    private shipments: OrderPickingService,
    private salesOrderService: SalesOrderService,
    private stockItems: StockItemService,
    private inventoryService: InventorySourceService,
    private customers: CustomerSerivce,
  ) {
    super();

    // get the shipment number from the navigation router
    this.route.paramMap.subscribe(params => {
      this.shipmentNbr = params.get('shipmentNbr');
    });
  }

  screenMenuItems = [
    {
      icon: 'shop',
      label: "Update Shipment to Acumatica",
      handler: () => {
        this.dialog.open(ConfirmationDialog, {
          data: {
            message: 'Update Shipment with current Pick Details to Acumatica?',
            confirmText: 'Yes, Update it'
          }
        }).afterClosed().subscribe(confirm => {
          if (!confirm) return;
          this.completeShipment(false);
        })
      }
    },

    {
      icon: 'visibility',
      label: 'Show Completed Lines',
      handler: () => this.showCompletedLines = true,
      condition: () => !this.showCompletedLines
    },
    {
      icon: 'visibility_off',
      label: 'Hide Completed Lines',
      handler: () => this.showCompletedLines = false,
      condition: () => this.showCompletedLines
    },

    {
      icon: 'restart_alt',
      label: 'Refresh Warehouse Locations',
      handler: () => {
        this.warehouses.getLocations(this.shipment.WarehouseID, true)
          .subscribe(locations => this.warehouseLocations = locations);
      }
    },

    {
      icon: 'print',
      label: "Print Quality Control Tags",
      handler: () => this.printQualityControlTags()
    },

    {
      icon: 'phone_in_talk',
      label: "Print Will Call Tag",
      handler: () => this.printCustomerWillCallTag()
    },

    {
      label: "Log Shipment to console",
      condition: isDevMode(),
      handler: () => {
        console.warn('Object', this.shipment);
        console.warn('Encoded', this.shipment!.encode());
        console.warn('Normalized', this.shipment.normalize());
      }
    },

    {
      label: 'Debug',
      icon: 'adb',
      condition: isDevMode(),
      handler: () => {
        console.warn(this.inventory);
      }
    },
  ]

  periodicUpdateCount = 0;
  ngOnInit() {
    // log the shipment data to the server automatically every 5 minutes
    let timer = setInterval(() => {
      let appendToShipmentNbr = (++this.periodicUpdateCount * 5) + ' minutes'
      this.log(appendToShipmentNbr)
    }, 5 * 60 * 1000);
    this.onPickingClosed.subscribe(() => clearInterval(timer));
  }

  /** local event emitted when the picking component is navigated away from */
  onPickingClosed = new EventEmitter<void>();
  ngOnDestroy() { this.onPickingClosed.emit(); }

  // #endregion

  // #region Debug / Development utilities
  consoleLog(...args: any[]) {
    console.log.apply(this, args);
  }

  copyStockQRToClipboard(inventoryID: string, LotSerialNbr: string) {
    let json = JSON.stringify({ "ITM": inventoryID, "LOT": LotSerialNbr });
    this.clipboard.copy(json);
    this.snackBar.open(json, undefined, { duration: 2000 });
  }
  // #endregion

  // #region Shipment loading, preparing, completing

  /** event emitted when the first incomplete line's picking panel has been opened during startup */
  firstPickingPanelOpened: EventEmitter<void> = new EventEmitter();

  private _shipmentNbr: string | null = null;
  get shipmentNbr() { return this._shipmentNbr; }
  set shipmentNbr(value) {
    if (!value) { this.router.navigate(['/picking']); return; }
    this._shipmentNbr = value;
    this.isDirty = true;

    // get & validate Shipment and start the picking process
    this.shipments.get(value, { loader: null }).subscribe({
      error: (error: any) => {
        if (error instanceof NotFoundError) error.message = "Not Found - Invalid Shipment Number or Shipment has been deleted";
        this.dialog.open(AlertDialog, {
          data: { icon: 'error', message: error?.message, buttonText: 'Ok' }
        });
        this.isDirty = false;
        this.router.navigate(['/picking']);
      },

      next: shipment => {
        // only "Open" or "On Hold" status Shipments can be picked
        if (shipment.Status != 'Open' && shipment.Status != 'On Hold') {
          this.dialog.open(AlertDialog, {
            data: { icon: 'error', message: "Shipment cannot be picked\nStatus is " + shipment.Status, buttonText: 'Ok' }
          });
          this.isDirty = false;
          this.router.navigate(['/picking']);
          return;
        }

        let beginPicking = () => {
          // begin shipment picking
          this.shipment = shipment;
          this.shipments.setCurrentPickerID(shipment);
          this.pageTitle.setValue("Picking: " + shipment.ShipmentNbr);
          if (shipment.isSampleOrder) setTimeout(() => this.memoInventoryID.nativeElement.focus());

          this.prepareShipmentForPicking(shipment);
        }

        // alert if there's already a different picker
        if (shipment.PickerID
          && shipment.PickerID != this.acumatica.username
          && this.acumatica.username?.toUpperCase() != 'NICKH') {
          this.dialog.open(ConfirmationDialog, {
            data: {
              message: 'This shipment has already been scanned into.\nTake over picking from -- ' + shipment.PickerID + '?',
              confirmText: 'Yes, start picking',
              cancelText: 'No, cancel'
            }
          }).afterClosed().subscribe(response => {
            if (!response) {
              this.router.navigate(['/']);
              return;
            } else {
              beginPicking();
            }
          });
        }
        else beginPicking();

      }
    });
  }

  get quickMemoEntryEnabled() { return !this.shipment?.isComplete && this.shipment?.isSampleOrder; }

  _inventoryDoneLoading: boolean = false;
  /** flag set to true when all Item and Inventory data is done loading */
  get inventoryDoneLoading() { return this._inventoryDoneLoading }
  set inventoryDoneLoading(value: boolean) {
    this._inventoryDoneLoading = value;
    if (value && this.shipment?.isSampleOrder) this.resetMemoPickingInput();
  }

  /** performs various operations on the Shipment and fetches supplemental data in preparation for the shipment to be worked */
  private prepareShipmentForPicking(shipment: Shipment) {
    // fetch the customer to check for custom labels and AcceptsPFAS indicator
    if (!shipment.isWhseTransfer) {
      this.customers.get(shipment.CustomerID, { loader: null, noCache: true }).subscribe(customer => {
        this.customer = customer;
      });
    }

    // fetch the warehouse loations to validate put-away locations
    this.warehouses.getLocations(shipment.WarehouseID).subscribe(locations => this.warehouseLocations = locations);

    // exclude lines that are completed picking
    // shipment.Details = shipment.Details.filter(detail => detail.OpenQty != 0);

    // sort the shipment line details into picking order
    shipment.Details.sort(this.sortShipmentDetailsForPickingComparator);

    // remove TARIFF-FEE items from shipment details eing worked
    this.nonStockItemHold = shipment.Details!.filter(detail => detail.InventoryID == 'TARIFF-FEE');
    this.nonStockItemHold.forEach(detail => {
      let alloc = new ShipmentDetailAllocation();
      alloc.Qty = detail.OrderedQty;
      detail.Allocations.push(alloc);
    });
    shipment.Details = shipment.Details!.filter(detail => detail.InventoryID != 'TARIFF-FEE')

    // once per unique Inventory ID retrieve the Stock Item and Inventory data
    // one at a time in picking sequence (reduce concurrent API requests)
    let inventoryIDs = shipment.Details!
      .filter(detail => !detail.isComplete) // do not need to initially fetch Item and Inventory for completed lines -- fetch them on-demand when panel is opened
      .map(line => line.InventoryID).unique<string>();
    let inventoryIDindex = -1;
    let inventoryLoadingSnackbarRef = this.snackBar.openFromComponent(FeedbackSnackbarComponent, { data: { message: "Getting Items..." } });
    let getNextItem = (openFirstPanel: boolean = false) => {
      if (++inventoryIDindex >= inventoryIDs.length) {
        this.inventoryDoneLoading = true;
        inventoryLoadingSnackbarRef.dismiss();
        return;
      }

      let inventoryID = inventoryIDs[inventoryIDindex];
      this.getItem(inventoryID, status => {
        inventoryLoadingSnackbarRef.instance.message = status;
      }).then(item => {
        let loadedInventory: Promise<any> =
          (this.shipment.isSampleOrder)
            ? this.getInventory(inventoryID, status => { inventoryLoadingSnackbarRef.instance.message = status; })
            : Promise.resolve();

        loadedInventory.then(() => {
          // add reference to ShipmentDetail for the corresponding StockItem
          shipment.Details
            .filter(line => line.InventoryID == inventoryID)
            .forEach(line => {
              line.item = item
              if (shipment.isSampleOrder) line.SuggestPickingSource(this.inventory[line.InventoryID]);
            });

          // automatically open the panel of the first item if it's incomplete
          if (openFirstPanel && !shipment.Details![0].isComplete && this.openShipmentDetailPanelIndex == null) {
            this.openExpansionPanel(0);
            setTimeout(() => {
              this.current.focusFirstInput();
              this.firstPickingPanelOpened.emit();
            }, 100)
          }

          getNextItem();
        });
      });
    };

    getNextItem(this.shipment!.isYardageOrder);

    // prepare each shipment line detail
    shipment.Details.forEach(detail => {

      // create a FormGroup for the pick entry form of the ShipmentDetail line
      // associate with the Shipmentdetail using the ShipmentDetail.LineNbr
      this.pickFormGroups[detail.LineNbr!] = new FormGroup({
        lotSerNbr: new FormControl({ value: null, disabled: !detail.isLotSerItem }),
        qty: new FormControl(),
      });

      // fetch the referenced Sales Order for the line
      this.loadSalesOrder(detail);

      // when the Allocations within a Shipment Detail are changed, re-render the Picks table
      detail.OnAllocationsChanged.subscribe(this.current.refreshPicksTable);
    });
  }

  /** comparator function for sorting the ShipmentDetail lines into logical picking order */
  private sortShipmentDetailsForPickingComparator(a: ShipmentDetail, b: ShipmentDetail) {
    // non-fully-picked lines go before fully-picked lines
    if (a.isComplete != b.isComplete)
      return (a.isComplete ? 1 : -1);

    // sort by Location Pick Priority
    if (a.DefaultShipLocationPriority != b.DefaultShipLocationPriority)
      return a.DefaultShipLocationPriority! - b.DefaultShipLocationPriority!;

    // sort by Location ID alphabetically
    if (a.DefaultShipLocationID != b.DefaultShipLocationID)
      return a.DefaultShipLocationID!.localeCompare(b.DefaultShipLocationID!);

    // sort pre-allocated lines first (can potentially use remainder on non-allocated pieces on subsequent lines)
    if ((a.InventoryID == b.InventoryID) && ((a.PreAllocation && true) != (b.PreAllocation && true)))
      return (a.PreAllocation ? -1 : 1);

    // multiple Inventory IDs in same location -- sort alphabetically to match pick ticket
    if (a.InventoryID != b.InventoryID)
      return a.InventoryID.localeCompare(b.InventoryID);

    // sort by Line # failing all that
    return (a.LineNbr || 0) - (b.LineNbr || 0);
  }

  /** update the Shipment to Acumatica */
  updateShipmentToAcumatica() { this.completeShipment(true); }

  // #endregion

  // #region Sales Order document references

  /** dictionary of Sales Orders referenced by the Shipment's details */
  salesOrder: { [key: string]: SalesOrder } = {};

  private _loadSalesOrderPromises: { [key: string]: Promise<SalesOrder> } = {}
  /** load the Sales Order from Acumatica; necessary to reference original SO Line notes */
  private loadSalesOrder(shipmentDetail: ShipmentDetail) {
    // function-hidden dictionary of promises for SOs being loaded
    const promises = this._loadSalesOrderPromises;
    const key = shipmentDetail.SOKey;

    if (promises[key] === undefined) promises[key] = new Promise<SalesOrder>((resolve, reject) => {
      delete (this.salesOrder[key]);
      this.salesOrderService.get(shipmentDetail.OrderType, shipmentDetail.OrderNbr, { loader: null }).subscribe(salesOrder => {
        this.salesOrder[key] = salesOrder;
        resolve(salesOrder);
      }, error => {
        console.error(error);
        reject(error);
      });
    });

    return promises[key];
  }

  // #endregion

  // #region Item & Inventory data

  warehouseLocations!: string[];
  stockItem: { [key: string]: StockItem } = {};
  inventory: { [key: string]: InventorySource[] } = {};
  /** return a boolean value indicating if the stock item and inventory data is done being fetched and picking can commence */
  itemPickingReady(inventoryID: string): boolean { return this.stockItem[inventoryID] !== undefined && this.inventory[inventoryID] !== undefined; }
  @ViewChild('selectLocationForLotSerNbrDialogTemplate') selectLocationForLotSerNbrDialogTemplate!: TemplateRef<any>;
  @ViewChild('selectLocationForMemoDialogTemplate') selectLocationForMemoDialogTemplate!: TemplateRef<any>
  @ViewChild('specifyMemoTransferQtyDialogTemplate') specifyMemoTransferQtyDialogTemplate!: TemplateRef<any>

  // // private _getItemAndInventoryPromises: {
  // //   [key: string]: Promise<{
  // //     item: StockItem,
  // //     inventory: InventorySource[]
  // //   }>
  // // } = {}
  private _getItemPromises: { [key: string]: Promise<StockItem> } = {}
  private _getInventoryPromises: { [key: string]: Promise<InventorySource[]> } = {}
  
  // // /** fetch the StockItem and Inventory Summary data for the specified inventory ID */
  // // private getItemAndInventory(inventoryID: string, feedback?: (status: string) => void): Promise<IGetItemAndInventoryResult> {
  // //   // this function is invoked during Shipment preparation to initially fetch data but is also invoked from identifyInventorySource to
  // //   // wait for item & inventory data to be available when triggered from the user using the Sample warehouse quick-entry form
  // //   // keep a collection of promises in progress and return the existing promise

  // //   // return the one from the dictionary if it exists, or...
  // //   return this._getItemAndInventoryPromises[inventoryID] ||=
  // //     // create and store the a new promise
  // //     new Promise<{ item: StockItem, inventory: InventorySource[] }>(resolve => {
  // //       // get the stock item
  // //       feedback?.call(this, "Getting <strong>" + inventoryID + "</strong> Item");
  // //       this.stockItems.get(inventoryID, { loader: null }).subscribe(item => {
  // //         this.stockItem[inventoryID] = item;

  // //         // get the inventory
  // //         feedback?.call(this, "Getting <strong>" + inventoryID + "</strong> Inventory");
  // //         this.inventoryService.get(inventoryID, this.shipment!.WarehouseID, null, { loader: null }).subscribe(inventory => {
  // //           inventory = this.prepareInventoryData(item, inventory);
  // //           resolve({ item, inventory });
  // //         });
  // //       });
  // //     })
  // // }

  /** Provides a Promise that is resolved after the given Stock Item and Inventory data is available */
  private getItemAndInventory(inventoryID: string, feedback?: (status: string) => void): Promise<IGetItemAndInventoryResult> {
    return new Promise<IGetItemAndInventoryResult>(resolve => {
      this.getItem(inventoryID).then(item => {
        this.getInventory(inventoryID).then(inventory => {
          console.warn('getItemAndInventory', inventory);
          resolve({ item: item, inventory: inventory });
        })
      });
    });
  }

  /** fetch the StockItem for the specified inventory ID */
  private getItem(inventoryID: string, feedback?: (status: string) => void): Promise<StockItem> {
    return this._getItemPromises[inventoryID] ||=
      new Promise<StockItem>(resolve => {
        feedback?.call(this, `Getting <strong>${inventoryID}</strong> Item`);
        this.stockItems.get(inventoryID, { loader: null }).subscribe(item => {
          this.stockItem[inventoryID] = item;
          resolve(item);
        });
      });
  }
  /** fetch the Inventory Summary for the specified inventory ID */
  private getInventory(inventoryID: string, feedback?: (status: string) => void): Promise<InventorySource[]> {
    // if inventory data has already been retrieved, return a resolved promise with that existing (manipulable) dataset
    if( this.inventory[inventoryID] ) return Promise.resolve(this.inventory[inventoryID]);

    return this._getInventoryPromises[inventoryID] ||=
      new Promise<InventorySource[]>(resolve => {
        feedback?.call(this, `Getting <strong>${inventoryID}</strong> Inventory`);
        this.inventoryService.get(inventoryID, this.shipment!.WarehouseID, null, { loader: null }).subscribe(inventory => {
          this.getItem(inventoryID, feedback).then(item => {
            inventory = this.prepareInventoryData(item, inventory);
            resolve( inventory );
          });
        })
      })
  }

  /** Clean up Acumatica Inventory data in preparation for reference in picking */
  private prepareInventoryData(stockItem: StockItem, inventory: InventorySource[]): InventorySource[] {
    const UNASSIGNED = '<UNASSIGNED>';

    // when inventory is pre-Allocated on the SO -- 1 YD preallocated; 5 YD total On Hand -- inventory data shows
    // Location: "<UNASSIGNED>" Lot #: (null)   On Hand: 0    Available: N    AvailForShipment: 0   SO Booked: -N   SO Allocated: 0   (on orders, not allocated)
    // Location: "<UNASSIGNED>" Lot #: LOTNUM   On Hand: 0    Available: -1   AvailForShipment: -1  SO Booked: 0    SO Allocated: 1
    // Location: "BINNBR"       Lot #: LOTNUM   On Hand: 5    Available: 5    AvailForShipment: 5   SO Booked: 0    SO Allocated: 0

    // filter out any inventory records in the Cut & Hold bins -- can not be used for order picking purposes
    inventory = inventory.filter(inventory => !inventory.LocationID?.startsWith("CH"));

    // merge the pre-allocation, <UNASSIGNED> quantities into their main lines
    // typically will only be one line for a given lot #, but possible to be in multiple locations
    // find any <UNASSIGNED> lines remaining (pre-allocations)
    inventory.filter(inventory => inventory.LocationID == UNASSIGNED && inventory.LotSerialNbr != null)
      .forEach(preAlloc => {
        // find the inventory lines in specific locations
        let lots = inventory.filter(inventory => inventory.LocationID != UNASSIGNED
          && inventory.LotSerialNbr?.trim().toUpperCase() == preAlloc.LotSerialNbr?.trim().toUpperCase())
        if (lots.length == 0) {
          // there was no shelved piece for this Lot #; at least not any with any quantity still on them
          // going to silently disregard this pre-allocated <UNASSIGNED> line; not add it into any main line
          return;
        }
        else if (lots.length > 1) {
          // multiple matches for this Lot # in specific bin locations
          // add these quantities into the Primary location if available, otherwise to the largest piece
          let primaryLocationID = this.stockItem[stockItem.InventoryID].DefaultIssueFrom;
          lots.sort((a, b) => {
            if (a.LocationID == primaryLocationID) return -1;
            if (b.LocationID == primaryLocationID) return 1;
            return b.QtyAvailable - a.QtyAvailable;
          });
        }

        let lot = lots[0];
        lot.QtyOnHand += preAlloc.QtyOnHand;
        lot.QtyAvailable += preAlloc.QtyAvailable;
        // lot.QtyAvailableForShipment += preAlloc.QtyAvailableForShipment;
        lot.AltSOPreAllocated -= preAlloc.QtyAvailable;
      });

    // remove all <UNASSIGNED> lines -- their quantities have been added in to their main line
    inventory = inventory.filter(inventory => inventory.LocationID != UNASSIGNED);

    // add the Pre-Allocated line details from this Shipment into the corresponding Inventory records
    // so that they're retained and available for reference when picking
    // also update the Shipment pre-allocation Location in case another picker has moved the given piece
    this.shipment.Details.filter(detail => detail.InventoryID == stockItem.InventoryID && detail.PreAllocation).forEach(detail => {
      let preAlloc = detail.PreAllocation!;
      let lots = inventory.filter(inventory => inventory.LotSerialNbr == preAlloc?.LotSerialNbr);
      if (lots.length == 0) {
        this.dialog.open(AlertDialog, {
          data: {
            message: "Shipment Detail is Pre-Allocated but no such Lot # was found\n" +
              "Inventory ID: " + detail.InventoryID + "\nLot #:" + preAlloc.LotSerialNbr
          }
        });
      }
      else {
        let lot = lots[0];
        preAlloc.LocationID = lot.LocationID;
        lot.QtyAvailable += preAlloc.Quantity;
        lot.AltSOPreAllocated -= preAlloc.Quantity;
      }
    })

    // remove any lines with nothing available for shipment
    // remove any lines that contain PFAS if the Shipment is marked not AllowPFAS
    inventory = inventory.filter(inventory =>
      (inventory.LocationID != UNASSIGNED && inventory.QtyAvailable > 0)
      && (this.shipment.AllowPFAS || inventory.PFASFree || !stockItem.isRolledGood)
    );

    // sort results by quantity available
    inventory = inventory.sort((a: InventorySource, b: InventorySource) => {
      // if quantities are equal
      if (a.QtyAvailable == b.QtyAvailable) {
        // prioritize contains-PFAS items
        if (!!a.PFASFree != !!b.PFASFree) return (!!a.PFASFree) ? 1 : -1;

        // show the lots in the default location first
        if (a.LocationID == stockItem.DefaultIssueFrom) return -1;
        if (b.LocationID == stockItem.DefaultIssueFrom) return 1;
      }

      // otherwise, sort by smallest piece
      return (a.QtyAvailable || 0) - (b.QtyAvailable || 0);
    });

    inventory.forEach(inventory => {
      // assign Stock Item object ref to each Inventory Soruce record (for use when printing labels given the Inventory Source object)
      inventory.stockItem = stockItem

      // Stock tags are printed forcing upper-case on Lot #s -- ensure Lot #s received from Acumatica are uppercase for 'Find Piece' to work
      if (inventory.LotSerialNbr) inventory.LotSerialNbr = inventory.LotSerialNbr?.toUpperCase();

      // when the Location ID or Adjustment Qty of an InventorySource is changed
      // track it in the Shipment's corresponding collection
      inventory.OnLocationIDChanged.subscribe(() => { this.shipment.RecordTransfer(inventory); });
      inventory.OnAdjustmentQtyChanged.subscribe(() => { this.shipment.RecordAdjustment(inventory); });
    });

    // store the inventory data in the item-inventory-dictionary
    this.inventory[stockItem.InventoryID] = inventory;

    return inventory;
  }

  @ViewChild('adjustInventoryDialogTemplate') adjustInventoryDialogTemplate!: TemplateRef<any>;
  @ViewChild('adjustmentInlineEditor') adjustmentInlineEditor!: InventoryAdjustmentEditorComponent;
  editInventoryAdjustment(inventory: InventorySource) {
    let item = this.current.detail!.item!;
    let originalAdjustmentQty = inventory.AdjustmentQty;
    this.dialog.open(this.adjustInventoryDialogTemplate, {
      data: {
        item: item,
        inventory: inventory,
      }
    })
      .afterClosed().subscribe((acceptAdjustment: boolean) => {
        if (!acceptAdjustment) inventory.AdjustmentQty = originalAdjustmentQty;
      });
  }

  @ViewChild('transferInventoryPieceDialogTemplate') transferInventoryPieceDialogTemplate!: TemplateRef<any>;
  @ViewChild('inventoryTransferEditor') inventoryTransferEditor!: InventoryLocationEditorComponent;
  transferInventoryPiece(inventorySource: InventorySource) {
    let item = this.current.detail!.item!;

    let dialogRef = this.dialog.open(this.transferInventoryPieceDialogTemplate, { data: { item: item, inventory: inventorySource } });
    dialogRef.afterOpened().subscribe(() => {
      this.inventoryTransferEditor.onLocationEntered.subscribe(value => {
        inventorySource.LocationID = value;
        dialogRef.close();
      });

    });
  }

  // #endregion

  // #region Expansion Panel / Selection management

  /** the index of the currently open Shipment Detail expansion panel or null when none are open */
  openShipmentDetailPanelIndex: number | null = null;
  @ViewChildren(MatExpansionPanel) matExpansionPanels!: QueryList<MatExpansionPanel>;

  closeCurrentExpansionPanel() {
    if (this.openShipmentDetailPanelIndex !== null)
      this.matExpansionPanels.get(this.openShipmentDetailPanelIndex)?.close();
  }

  /** handler for when a Shipment Detail mat-expansion-panel is opened by the user */
  onShipmentDetailPanelOpened(
    detail: ShipmentDetail,
    picksTable: MatTable<ShipmentDetailAllocation>,
    index: number
  ) {
    this.openShipmentDetailPanelIndex = index;

    // wait for the panel expansion animation and (closed) event of previously opened panel to fire
    setTimeout(() => {

      // wait for the item and inventory data to be loaded in case of anxious user
      this.getItemAndInventory(detail.InventoryID).then(() => {

        // track at component level the current ShipmentDetail that's opened
        this.current.detail = detail;
        detail.item = this.current.item = this.stockItem[detail.InventoryID];
        this.current.picksTable = picksTable;

        // update suggested picking source in case other lines modified the availability
        detail.SuggestPickingSource(this.inventory[detail.InventoryID]);

        // set focus to the appropriate input field
        if (!detail.isComplete) this.current.focusFirstInput();

        // pre-load inventory of 'next' item to be worked
        let nextPick = this.identifyNextLineToBePicked();
        if (nextPick != null) {
          this.getInventory(this.shipment!.Details[nextPick].InventoryID);
        }
      });
    }, 10);
  }

  // handler for when a Shipment Detail mat-expansion-panel is closed
  onShipmentDetailPanelClosed(index: number, shipmentDetail: ShipmentDetail) {
    this.openShipmentDetailPanelIndex = null;
    this.current.detail = null;
    this.current.item = null;
    this.current.picksTable = null;
  }

  /** open the indicated expansion panel */
  openExpansionPanel(panel: number | MatExpansionPanel | ShipmentDetail) {
    // close previously opened panel if any
    if (this.current.detail)
      this.matExpansionPanels.get(this.shipment.Details.indexOf(this.current.detail))?.close();

    // open indicated panel
    if (panel instanceof ShipmentDetail) panel = this.shipment.Details.indexOf(panel);
    if (typeof panel == 'number') panel = this.matExpansionPanels.get(panel)!;
    panel.open();
  }

  identifyNextLineToBePicked() {
    // determine the index to start checking from
    let lastIndex = this.current.detail
      // check from the 'current' detail in case it's not yet complete
      ? this.shipment.Details.indexOf(this.current.detail)
      : 0; // nothing is currently opened -- shipment initially loaded

    let checkIndex = lastIndex;
    do {
      if (++checkIndex == this.shipment!.Details.length) {
        // reset to check from the start
        checkIndex = 0;
        // unless this was invoked by the initial shipment loading and the first panel is already being opened
        if (lastIndex == -1) return null;
      }

      // when all lines from the start have been checked
      // entire order has been checked and is compelete
      if (checkIndex == lastIndex) return null;
    } while (this.shipment.Details[checkIndex].isComplete);

    return checkIndex;
  }

  /** identify the next line to be worked and open the corresponding expansion panel */
  openNextLineToBePickedPanel() {
    // memo sample department does not automatically open panels
    if (this.shipment.isSampleOrder) return;

    // scroll to the top [Confirm Shipment] button
    if (this.shipment.isComplete) {
      this.closeCurrentExpansionPanel();
      this.scrollToTop();
      return;
    }

    let nextPanelIndex = this.identifyNextLineToBePicked();

    if (nextPanelIndex != null) this.openExpansionPanel(nextPanelIndex);
  }

  // #endregion

  // #region Expansion Panel Pick Entry form

  /**
   * Get the InventorySource record for the indicated Inventory ID and Lot/Serial Nbr.
   * If the Lot/Serial Nbr is invalid for a yardage item the return Promise will be rejected.
   * If the Lot # exists in multiple locations the user is prompted to select Location.
   * If a memo item is not available in the primary location the user is prompted to select from available Locations.
   * dialogClosedSubscriber is invoked after a Location Selection dialog is closed and is used to set focus to Qty input.
   */
  getInventorySourceForItem(inventoryID: string, lotSerNbr: string | null = null, dialogClosedSubscriber?: () => void): Promise<InventorySource> {
    return new Promise<InventorySource>((resolve, reject) => {

      // callback handler for 'select location' dialog prompt completion
      let complete = (source: InventorySource) => {
        resolve(source);
        dialogClosedSubscriber?.call(this);
      }

      this.getItemAndInventory(inventoryID).then(result => {
        let item = this.current.item = result.item;
        let inventory = result.inventory.filter(i => i.LotSerialNbr == lotSerNbr &&
          /* when a PO is linked to fill multiple SO Lines, after the first is worked the roll may be placed in its 'home' location and the
             cut piece put in Cut & Hold. The next shipment can assume to come from the 'home' location since it will exist in multiple */
          (this.current.detail?.PreAllocation == undefined || i.LocationID == this.current.detail?.PreAllocation?.LocationID));

        if (inventory.length == 0) reject(
          this.shipment.isYardageOrder
            ? "Lot/Serial # \"" + lotSerNbr + "\" is not valid for Inventory Item \"" + inventoryID + "\""
            : "No inventory available\nfor item \"" + inventoryID + "\""
        );
        else if (inventory.length == 1) {
          // no memos in primary bin, but all in alternative (overstock) location
          if (this.shipment.isSampleOrder && inventory[0].LocationID != item.DefaultIssueFrom)
            this.doMemoTransferToPrimaryLocation = true;

          resolve(inventory[0]);
        }
        else {
          // multiple options
          if (this.shipment.isSampleOrder) {
            // check for inventory in the primary / default bin location
            let source = inventory.find(inv => inv.LocationID == item.DefaultIssueFrom);
            if (source) {
              complete(source);
              return;
            }
          }

          // sort them for use in the Location selection dialog
          inventory = inventory.sort((a, b) => a.LocationID!.localeCompare(b.LocationID!));

          if (this.shipment.isSampleOrder)
            this.doMemoTransferToPrimaryLocation = true;

          this.sounds.play(Sound.GentleBeep);
          this.dialog.open(
            this.shipment.isYardageOrder
              ? this.selectLocationForLotSerNbrDialogTemplate
              : this.selectLocationForMemoDialogTemplate,
            { data: { inventory: inventory } }
          ).afterClosed().subscribe(complete);
        }
      });
    });
  }

  // handler for a Lot / Serial # being entered in the pick entry form -- determine Inventory Source, set up Quick Action
  onLotSerialNbrEntered(lotNbr: string | number | null, shipmentDetail: ShipmentDetail): Promise<void> {
    if (!lotNbr) return Promise.resolve();

    // set or clear the detail.PickSource accordingly
    return this.getInventorySourceForItem(shipmentDetail.InventoryID, <string>lotNbr, this.current.Qty.focus).then(
      source => {
        // store reference to the identified InventorySource
        this.current.source = source;

        // validate the source if the ShipmentDetail was pre-allocated
        if (shipmentDetail.PreAllocation && (
          shipmentDetail.PreAllocation.LotSerialNbr != source.LotSerialNbr ||
          shipmentDetail.PreAllocation.LocationID != source.LocationID)) {
          this.alertDialog("This Line is Pre-Allocated.\nThe indicated inventory source must be used.").afterClosed().subscribe(this.current.LotSerNbr.focus);
          this.current.LotSerNbr.reset();
          return;
        }

        // initialize 'Quick Action' button

        // Pre-Alloction; quick button for the PreAllocation ship quantity
        if (shipmentDetail.PreAllocation) {
          shipmentDetail.QuickAction = {
            label: shipmentDetail.PreAllocation.Quantity + ' ' + shipmentDetail.UOM,
            action: () => {
              let alloc = this.current.detail!.NewAllocation(source);
              alloc.Qty = shipmentDetail.PreAllocation!.Quantity;
              this.validatePiecePicking(alloc);
            }
          }
          return;
        }

        // automatic End-Of-Roll allowance
        let EORAllowance = shipmentDetail.item!.isLotSerItem ? Math.max(0, source.AdjQtyAvailable - shipmentDetail.OpenQty) : 0;
        if (EORAllowance > 0
          && EORAllowance < AppConfig.settings.EndOfRollAutoShip
          && (source.AdjQtyOnHand - source.SOAllocated) == source.AdjQtyAvailable) {
          shipmentDetail.QuickAction = {
            label: 'EOR ' + source.AdjQtyAvailable + ' ' + shipmentDetail.UOM,
            action: () => {
              let alloc = this.current.detail!.NewAllocation(source);
              alloc.Qty = Math.min(source.AdjQtyAvailable, shipmentDetail.OpenQty + EORAllowance);
              alloc.EOR = true;
              alloc.EORQty = EORAllowance;
              this.validatePiecePicking(alloc);
            }
          }
          return;
        }

        let qty =
          // when set to Force Incomplete (sending additional small pieces), use the remaining piece size
          shipmentDetail.forceIncomplete ? source.AdjQtyAvailable
            // otherwise, smaller of Quantity Available for Inventory Source or remaining Open Quantity for the order
            : Math.min(source.AdjQtyAvailable, shipmentDetail.OpenQty);
        shipmentDetail.QuickAction = {
          label: qty + ' ' + shipmentDetail.UOM,
          action: () => {
            let alloc = this.current.detail!.NewAllocation(source);
            alloc.Qty = qty;
            this.validatePiecePicking(alloc);
          }
        }
      },
      // rejected
      reason => {
        if (reason) this.alertDialog(reason).afterClosed().subscribe(this.current.focusFirstInput);
        this.current.resetForm();
      }
    );
  }

  // handler for the 'Edit Pick' button
  onPickEntryEditDetails() {
    let alloc = this.current.createAllocation();
    this.validatePiecePicking(alloc, true);
  }

  // handler for the [OK] Submit button / form submit action
  onPickEntrySubmit() {
    let validateInventorySource = this.current.detail?.isLotSerItem
      // lot/serial items are validated to have a source by the enabled/disabled function of the [OK] submit button
      ? Promise.resolve()
      // when entering a quantity into the expansion panel of a memo item, Inventory Source has not yet been identified
      : this.getInventorySourceForItem(this.current.detail!.InventoryID).then(source => this.current.source = source);

    validateInventorySource.then(() => {
      console.warn('onPickEntrySubmit > validateInventorySource', this.current.source);

      let alloc = this.current.createAllocation();
      this.validatePiecePicking(alloc);
    });

  }

  /**
   * Validate the provided allocation and open the pick detail editor if necessary. Proceed to Put-Away
   * @param allocation the ShipmentDetailAllocation object to validate
   * @param openPickDetailEditor flag indicating to open the detail editor even if not necessary (user manually opened)
   */
  validatePiecePicking(allocation: ShipmentDetailAllocation, openPickDetailEditor = false) {
    this.current.resetForm();

    // when shipping full rolls but the entered quantity doesn't match the piece size
    if (this.current.detail!.ShipFullRolls) {
      let variance = -allocation.source!.AdjQtyAvailable;
      if (variance != 0) {
        // initialize Adjustment to set remaining stock to zero
        allocation.source!.AdjustmentQty = variance;
        openPickDetailEditor = true;
      }
    }

    // the line was over-picked
    else if (this.current.detail!.OpenQty < 0) {
      // user must specify reason for over-picking
      openPickDetailEditor = true;
    }

    // pick quantity exceeds available quantity -- incorrect inventory
    else if (allocation.source!.AdjQtyAvailable < 0) {
      // initialize Adjustment to set remaining stock to zero
      allocation.source!.AdjustmentQty = -allocation.source!.AdjQtyAvailable
      openPickDetailEditor = true;
    }


    if (openPickDetailEditor) {
      this.openPickDetailEditor(allocation, false).then(result => {
        // dialog was closed by [OK] button
        if (result == true) this.openPickCompletionDialog(allocation);
        // allocation was Deleted in detail editor
        else this.current.resetForm();
      });
    }

    // pick entry complete
    else this.openPickCompletionDialog(allocation);
  }
  // #endregion

  // #region Pick Details Editor and Pick Completion Dialogs

  /** Opens the Pick Details editor. Returns a Promise<boolean> resolved when the dialog is closed.
      The promise results in true when closed by the [OK] button or false when the allocation was [Deleted] */
  openPickDetailEditor(allocation: ShipmentDetailAllocation, editingEstablishedPick: boolean): Promise<boolean> {
    return new Promise<boolean>(resolve => {

      let dialogRef = this.dialog.open(PickDetailEditorComponent, {
        data: {
          detail: this.current.detail,
          allocation: allocation,
          inventory: this.inventory[this.current.detail!.InventoryID],
          editingEstablishedPick: editingEstablishedPick
        }
      });
      dialogRef.afterClosed().subscribe(result => {
        resolve(result);
        onPickingClosed.unsubscribe();
      });

      // close the dialog if the component is navigated away from
      let onPickingClosed = this.onPickingClosed.subscribe(() => dialogRef.close());
    });
  }

  /** handler for the "Edit Pick Details" menu button */
  onPickMenuOpenPickDetailEditor(allocation: ShipmentDetailAllocation) {
    this.openPickDetailEditor(allocation, true).then(() => {
      if (!this.current.detail?.isComplete) this.current.resetForm();
    })
  }

  /** open the dialog for adjustments, printing labels, and restocking */
  openPickCompletionDialog(allocation: ShipmentDetailAllocation) {
    // memo items don't need restocked / put-away dialog
    if (!this.current.detail?.isLotSerItem) return;

    let dialogRef: MatDialogRef<PickCompletionDialogComponent> =
      this.dialog.open(PickCompletionDialogComponent, {
        data: {
          shipment: this.shipment,
          allocation,
          warehouseLocations: this.warehouseLocations
        }
      });

    dialogRef.componentInstance.PrintTagsLabels.subscribe(printerID => this.printPieceLabels(allocation, printerID));

    dialogRef.afterClosed().subscribe(closeReason => {
      onPickingClosed.unsubscribe();
      // allocation.source!.AdjustmentQty = dialogRef.componentInstance.adjustmentAmount;

      switch (closeReason) {
        case 'editAllocation':
          this.validatePiecePicking(allocation, true);
          break;
        case 'reusePiece':
          let reusableOnShipmentDetail = dialogRef.componentInstance.reuseLineDetail!;

          this.dialog.open(PrinterSelectDialog, {
            data: {
              ConfirmButtonText: 'Print',
              Title: "Print Customer Roll Tag"
            }
          }).afterClosed().subscribe(result => {
            if (result?.Printer) {
              this.printCutAndHoldTag(allocation, result.Printer.id).then(() => {
                this.printCustomerRollLabel(allocation, result.Printer.id);
              });
            }
            this.openExpansionPanel(reusableOnShipmentDetail);
          });

          break;

        default:
          if(this.current.detail?.isComplete)
            this.openNextLineToBePickedPanel();
          else
            this.current.focusFirstInput();
      }
    });

    let onPickingClosed = this.onPickingClosed.subscribe(() => dialogRef.close());
  }

  // #endregion

  // #region Memo/Card direct Inventory ID / scan entry

  @ViewChild('memoInventoryID') memoInventoryID!: ElementRef;
  memoPickInventoryID: string | null = null;
  memoPickQty: string | null = null;
  /** field implemented in the dialog prompting for memo overstock location to take from, for user to indicate transfer to primary location */
  doMemoTransferToPrimaryLocation: boolean = false;
  memoPickTransferQty: number | null = null;

  /** handler for user selecting to transfer memos to their primary location when taking from an overstock location */
  transferMemosToPrimaryLocation(source: InventorySource) {
    if (!this.doMemoTransferToPrimaryLocation) return Promise.resolve()

    return new Promise<void>(resolve => {
      this.dialog.open(this.specifyMemoTransferQtyDialogTemplate, {
        data: {
          source: source
        }
      }).afterClosed().subscribe(strQty => {
        let qty = parseInt(strQty);

        let transfer = Object.assign(new InventorySource(), source);
        transfer.OriginalLocationID = source.LocationID;
        transfer.LocationID = source.stockItem.DefaultIssueFrom;
        transfer.QtyAvailable = qty;
        transfer.QtyOnHand = qty;
        source.QtyOnHand -= qty;
        source.QtyAvailable -= qty;
        this.shipment!.RecordTransfer(transfer);

        // source.LocationID = this.current.detail!.item!.DefaultIssueLocationID;
        resolve();
      });

      this.doMemoTransferToPrimaryLocation = false;
      this.memoPickTransferQty = null;
    });
  }

  // handler for Samples warehouse, direct Inventory ID entry form
  onMemoPickFormSubmit() {
    if (!this.memoPickInventoryID) return;

    let inventoryID = this.memoPickInventoryID.toUpperCase();

    if (inventoryID == 'WC920') inventoryID = 'WC920CD'; // Editor
    if (inventoryID == '336D') inventoryID = '336CD'; // Editor

    // find a Shipment Line Detail for the inventoryID as provided
    let detailIndex = this.findIncompleteLineDetail(inventoryID);
    // didn't find one, try prepending 'M' and look again
    if (detailIndex === undefined) {
      detailIndex = this.findIncompleteLineDetail('M' + inventoryID);
      if (detailIndex !== undefined) inventoryID = 'M' + inventoryID;
    }

    // still didn't find one
    if (detailIndex === undefined) {
      this.alertDialog("There are no incomplete lines for Inventory ID \"" + inventoryID + "\"")
        .afterClosed().subscribe(() => this.resetMemoPickingInput());
      return;
    }

    // set reference to the shipment line detail
    let detail = this.current.detail = this.shipment.Details[detailIndex];

    // determine source location
    this.getInventorySourceForItem(inventoryID).then(source => {

      let createPickAllocation = () => {
        // create an Allocation entry for the line if it needs one
        if (detail.Allocations.length == 0) {
          detail.NewAllocation(source);
        }
        // "03" items only ever get one Allocation record
        let alloc = detail.Allocations[0];
        // increment the pick count
        alloc.Qty = (alloc.Qty || 0) + 1;
        // UI feedback
        this.memoPickQty = `${alloc.InventoryID}<br />${alloc.Qty} of ${detail.OrderedQty}`;
        // refresh UI tables
        detail!.Allocations = detail!.Allocations.concat();

        // move completed picks to bottom of list
        if (detail.isComplete) {
          let removed = this.shipment.Details.splice(detailIndex!, 1)[0];
          this.shipment.Details.push(removed);
        }

        // reset for next item
        this.resetMemoPickingInput();
      }

      if (this.doMemoTransferToPrimaryLocation) this.transferMemosToPrimaryLocation(source).then(createPickAllocation);
      else createPickAllocation();
    }).catch(message => {
      this.alertDialog(message).afterClosed().subscribe(() => this.resetMemoPickingInput());
    })
  }

  /** find and return an incomplete line detail for the given memo item Inventory ID */
  findIncompleteLineDetail(inventoryID: string): number | undefined {
    let index = this.shipment.Details.findIndex(detail => !detail.isComplete && detail.InventoryID == inventoryID);
    return (index != -1) ? index : undefined;
  }

  /** clear and set focus to the Memo item scanner input */
  resetMemoPickingInput() {
    this.memoPickInventoryID = null;
    setTimeout(() => this.memoInventoryID.nativeElement.focus());
  }

  // #endregion

  // #region Printing / Labels

  printPieceLabels(allocation: ShipmentDetailAllocation, printerID?: string): Promise<void> {
    return this.printStockRollTag(allocation, printerID).then(() => {
      this.printCutAndHoldTag(allocation, printerID).then(() => {
        this.printCustomerRollLabel(allocation, printerID);
      });
    });
  }

  printStockRollTag(allocation: ShipmentDetailAllocation | InventorySource, printerID?: string): Promise<void> {
    return new Promise<void>(resolve => {
      let inventory: InventorySource =
        (allocation instanceof ShipmentDetailAllocation) ? allocation.source! : allocation;

      let cutCount =
        // if doing an adjustment, set cut count to zero
        inventory.AdjustmentQty !== undefined
          ? 0
          // otherwise, print the tag with an incremented cut count if the piece has been used on this shipment
          : inventory.UsageCount > 0
            ? (inventory.CutCount || 0) + 1
            // (could print tags for pieces not being used on shipment)
            : inventory.CutCount;

      this.wms.selectPrinter(printerID).subscribe(printer => {
        if (printer === undefined || (inventory.AdjQtyOnHand - inventory.SOAllocated) == 0) { resolve(); return; }
        this.wms.printStockRollTag(
          printer.Printer.id,
          inventory.InventoryID,
          inventory.LotSerialNbr!,
          inventory.RollBalance + (inventory.AdjustmentQty || 0),
          undefined,
          undefined,
          undefined,
          cutCount,
          {
            indicator: LoadingIndicator.snackbar,
            message: "Printing Stock Tag"
          }
        ).subscribe(resolve);
      });

    });
  }

  printCutAndHoldTag(allocation: ShipmentDetailAllocation, printerID?: string): Promise<void> {
    if (!this.shipment.CutAndHold) return Promise.resolve();

    return new Promise<void>(resolve => {
      this.wms.selectPrinter(printerID).subscribe(printer => {
        if (printer === undefined) { resolve(); return; }
        this.wms.printCutAndHoldTag(
          printer.Printer.id,
          this.shipment.ShipmentNbr,
          allocation.shipmentDetail.LineNbr!,
          allocation.LotSerialNbr!,
          allocation.Qty!
        ).subscribe(resolve);
      });
    });
  }

  printCustomerRollLabel(allocation: ShipmentDetailAllocation, printerID?: string): Promise<void> {
    return new Promise<void>(resolve => {
      this.wms.selectPrinter(printerID).subscribe(printer => {
        if (printer === undefined) { resolve(); return; }

        let detail = allocation.shipmentDetail;

        return this.wms.printShipmentRollTag(
          printer.Printer.id,
          this.shipment!.ShipmentNbr,
          detail.LineNbr!,
          allocation.LotSerialNbr || '',
          allocation.FulfillmentQty,
          allocation.AllowanceQty,
          {
            indicator: LoadingIndicator.snackbar,
            message: "Printing Customer Roll Tags"
          }
        ).subscribe(resolve);
      });

    });
  }

  printCustomerWillCallTag(printerID?: string) {
    return new Promise<void>(resolve => {
      this.wms.selectPrinter(printerID, 'Print Will Call Tags', {Label: '# of Tags', Max: 20}).subscribe(result => {
        if (result === undefined) { resolve(); return; }

        return this.wms.printCustomerWillCallTag(
          result.Printer.id,
          this.shipment!.ShipmentNbr,
          result.NumberOfCopies ?? 1,
          {
            indicator: LoadingIndicator.snackbar,
            message: "Printing Will Call Tag"
          }
        ).subscribe(resolve);
      });

    });
  }

  // #endregion

  // #region Other miscellaneous operations
  noticeDialog(message: string, buttonText: string = "OK"): MatDialogRef<AlertDialog, any> {
    return this.alertDialog(message, buttonText, Sound.GentleBeep, null);
  }
  alertDialog(message: string, buttonText: string = "OK", sound: Sound = Sound.ErrorBeep, icon: string | null = 'warning'): MatDialogRef<AlertDialog, any> {
    return this.dialog.open(AlertDialog, {
      data: { message, buttonText, sound, icon }
    });
  }

  editNote(detail: ShipmentDetail) { this.acumatica.editNote("Shipment Detail", detail); }

  /** removes the Pre-Allocated piece requirement from the Shipment Detail */
  ignorePreAllocation(detail: ShipmentDetail) {
    this.dialog.open(ConfirmationDialog, { data: { message: "Please Confirm it is safe to remove the indicated Pre-Allocated piece from this line." } })
      .afterClosed().subscribe(confirm => {
        if (!confirm) return;

        detail.Allocations.remove(detail.Allocations[0]);
        detail.PreAllocation = undefined;
      });
  }

  enableAddingPicksToCompletedLine(detail: ShipmentDetail) {
    detail.forceIncomplete = true;
    this.current.LotSerNbr.focus();
  }

  toggleLotDisplay(inventory: any) {
    this.displayHashCodes = !this.displayHashCodes;
  }
  // #endregion

  // #region Complete Shipment & feedback dialog

  @ViewChild('shipmentCompletionStatus') shipmentCompletionStatusTemplate!: TemplateRef<any>; // Completion processing progress feedback panel

  /**
   * Update the Shipment to Acumatica.
   * Returns a Promise resolved when the shipment is successfully updated to Acumatica, Rejected when nothing to update or update fails
   * @param isComplete Specifies if the shipment is done being picked or is being updated to resume picking later
   */
  completeShipment(isComplete: boolean): Promise<void> {

    return new Promise<void>((resolve, reject) => {

      // prevent completing shipments with zero quantity
      if (this.shipment.totalPickedQty == 0 && !this.shipment.hasTransfers && !this.shipment.hasAdjustments) {
        this.dialog.open(AlertDialog, {
          data: {
            message: "Nothing to update"
          }
        });
        reject();
        return;
      }

      this.completeShipment_ConfirmPrinter().then((printer: IPrinter | null) => {
        this.GetCutAndHoldLocation().then(cutAndHoldLocationID => {
          this.changeDetector.detach();

          this.nonStockItemHold.forEach(detail => this.shipment.Details.push(detail));

          this.shipments.completeShipmentPicking(
            this.shipment,
            // OBSOLETE -- revised to print Customer-specific roll tags from Acumatica Action with each Customer Roll Tag
            // // when the customer has custom labels, if this is a cut&hold use the printer indicated in storage, otherwise the one selected
            // this.customer?.CustomLabelReportID && (this.shipment.CutAndHold ? this.CutAndHoldCustomerTagPrinterID : printer?.id),

            isComplete,
            cutAndHoldLocationID,
            status => this.completeShipment_ShowProgressFeedback(status)
          ).subscribe({
            next: () => { this.completeShipment_Successful(); resolve() },
            error: err => { this.completeShipment_Error(err); reject() }
          })
        }, this.noop);
      });
    });

  }

  completeShipment_Successful = () => {
    this.shipmentCompletionDialogRef!.close();
    this.dialog.open(AlertDialog, {
      data: {
        message: "Shipment has been updated",
        buttonText: 'Ok'
      }
    }).afterClosed().subscribe(() => {
      this.isDirty = false;
      this.router.navigate(['/picking']);
    });
  };

  completeShipment_Error = (errObject: any) => {
    this.nonStockItemHold.forEach(detail => this.shipment.Details.remove(detail));

    this.changeDetector.reattach();

    // error object explicitly generated and thrown by the Shipment.services errHandler routine such as in an sync operation
    if (errObject.error !== undefined) {
      // log and get the user-friendly message from the error
      let userErrorMessage = this.errorHandler.handleError(errObject.error, false, this.shipment.normalize());
      // update the status feedback object (bound to the UI), error message property with the user-friendly one
      errObject.status.error = userErrorMessage;
    }

    // some other type of general, unexpected error
    else {
      let userErrorMessage = this.errorHandler.handleError(errObject, false, this.shipment.normalize());
      this.completeShipment_ShowErrorFeedback(userErrorMessage);
    }
  };

  /** Prompts for and returns a printer to use for Customer Pin tags, if needed (unless shipment is a Cut & Hold) */
  completeShipment_ConfirmPrinter(): Promise<IPrinter | null> {
    return new Promise<IPrinter | null>((resolve, reject) => {

      if (!this.customer?.CustomLabelReportID || this.shipment.CutAndHold) { resolve(null); return; }
      if (this.wms.lastPrinterUsed !== undefined) { resolve(this.wms.lastPrinterUsed); return; }

      this.dialog.open(PrinterSelectDialog, {
        data: { Title: "Print Customer Pin Tags" }
      }).afterClosed().subscribe(result => {
        if (result) {
          resolve(result.Printer);
        } else {
          this.dialog.open(ConfirmationDialog, {
            data: {
              message: "Labels not printed\nContinue with updating Shipment?",
              confirmText: 'Yes, Continue',
              cancelText: 'No, Cancel',
            }
          }).afterClosed().subscribe(response => {
            if (response) resolve(null);
            else reject();
          })
        }
      });

    });
  }

  private shipmentCompletionDialogRef?: MatDialogRef<any>;
  private _lastStatus: CompleteShipmentStatus | undefined;
  completeShipment_ShowProgressFeedback(status: CompleteShipmentStatus) {
    // first time progress feedback is received, open the dialog
    if (this.shipmentCompletionDialogRef === undefined) {
      this.shipmentCompletionDialogRef = this.dialog.open(this.shipmentCompletionStatusTemplate, {
        data: {
          status: status
        }
      });

      this._lastStatus = status;
    }
  }

  completeShipment_ShowErrorFeedback(userErrorMessage: string) {
    if (this._lastStatus) {
      this._lastStatus.error = userErrorMessage;
      this.completeShipment_ShowProgressFeedback(this._lastStatus);
    }
  }

  CloseProgressFeedback() {
    this.shipmentCompletionDialogRef?.close();
    this.shipmentCompletionDialogRef = undefined;
  }

  log(appendToShipmentNbr: string = '') {
    if (this.shipment == undefined) return;

    let plainShipment = this.shipment.normalize();
    let label = this.shipment.ShipmentNbr + (appendToShipmentNbr
      ? (' - ' + appendToShipmentNbr)
      : '');

    this.logger.log(
      this.acumatica.username,
      label.trim(),
      JSON.stringify(plainShipment)
    );
  }

  // #endregion

  // #region Quality Control Tags
  @ViewChild('printQualityControlTagsDialogTemplate') printQualityControlTagsDialogTemplate!: TemplateRef<any>;
  printQualityControlTags() {
    this.dialog.open(PrinterSelectDialog, {
      data: {
        Title: "Quality Control Labels",
        message: "This process will print a large number of labels. Only run this if you're sure you know what you're doing.",
      }
    }).afterClosed().subscribe((result: { Printer: { id: string } }) => {
      if (!result.Printer) return;

      this.completeShipment(true).then(() => {
        this.shipments.printQualityControlLabels(this.shipment, result.Printer.id, {
          message: 'Printing Quality Control Tags'
        })
      })
    });
  }
  // #endregion

  // #region Cut & Hold
  @ViewChild('CutAndHoldLocationDialogTemplate') CutAndHoldLocationDialogTemplate!: TemplateRef<any>;
  CutAndHoldLocationDialog?: MatDialogRef<any, any>;
  CutAndHoldLocation = new FormControl('');

  private cutAndHoldCustomerTagPrinterKey = 'cut-hold-customer-rolltag-last-printer';
  /** stores to local device the printer used for printing customer-specific roll tags on Cut & Hold orders */
  get CutAndHoldCustomerTagPrinterID(): string | null {
    return localStorage.getItem(this.cutAndHoldCustomerTagPrinterKey);
  }
  set CutAndHoldCustomerTagPrinterID(value: string | null) {
    if (!value) localStorage.removeItem(this.cutAndHoldCustomerTagPrinterKey);
    else localStorage.setItem(this.cutAndHoldCustomerTagPrinterKey, value)
  }

  /** handler for [Enter] or Scan Input on Cut & Hold dialog, Location field */
  onCutAndHoldLocationEntered(locationID: string | number | null) {
    if (!locationID) return;
    locationID = locationID.toString().toUpperCase().trim();
    if (this.warehouseLocations.contains(locationID))
      this.CutAndHoldLocationDialog?.close(true);
    else {
      this.sounds.play(Sound.GentleBeep);
      this.CutAndHoldLocation.reset();
      (<any>this.CutAndHoldLocation).focus();
    }
  }

  /** opens the dialog for the user to provide the Cut & Hold bin goods are being put into. Promise is rejected on [Cancel] */
  GetCutAndHoldLocation(): Promise<string> {
    if (!this.shipment.CutAndHold) return Promise.resolve('');

    return new Promise<string>((resolve, reject) => {
      this.CutAndHoldLocationDialog = this.dialog.open(this.CutAndHoldLocationDialogTemplate);
      this.CutAndHoldLocationDialog.afterOpened().subscribe(() => (<any>this.CutAndHoldLocation).focus());
      this.CutAndHoldLocationDialog.afterClosed().subscribe(response => {
        if (response) {
          resolve(this.CutAndHoldLocation.value);
        } else {
          this.CutAndHoldLocation.reset();
          reject();
        }
      });
    });
  }
  // #endregion

  // #region Non-Stock Item (TARIFF-FEE)
  
  // #endregion
}

// #region "Current" container object interface

interface ICurrentEntryForm {
  /** reference to the ShipmentDetail object of the currently opened expansion panel */
  detail: ShipmentDetail | null,
  /** reference to the StockItem object of the currently opened expansion panel */
  item: StockItem | null,
  /** holder for the current entry's form's identified Inventory Source */
  source: InventorySource | null,
  /** reference to the currently opened panel's picksTable */
  picksTable: MatTable<ShipmentDetailAllocation> | null,
  /** return the FormGroup for the currently opened ShipmentDetail panel */
  formGroup: () => FormGroup,
  /** return the FormControl of the currently opened ShipmentDetail panel specified by the path */
  getControl: (path: string) => Promise<AbstractControl>,
  /** set focus to the Lot Serial Nbr (for Tracked items) or the Qty (for non-Tracked items) */
  focusFirstInput: () => void,
  /** re-render the Picks table to show changes to Allocations */
  refreshPicksTable: () => void,
  /** reset the form controls and the Inventory Source */
  resetForm: () => void,

  /** create and return a Shipment Detail Allocation (Pick) from the current form data */
  createAllocation: () => ShipmentDetailAllocation,

  /** reference to the Lot/Serial Number input field / FormControl */
  LotSerNbr: ICurrentEntryFormControlString,
  /** reference to the Qty input field / FormControl */
  Qty: ICurrentEntryFormControlNumeric
}
interface ICurrentEntryFormControlNumeric extends ICurrentEntryFormControl {
  /** return the value of the current entry form's field control */
  value: () => number | null,
}
interface ICurrentEntryFormControlString extends ICurrentEntryFormControl {
  /** return the value of the current entry form's field control */
  value: () => string | null,
}
interface ICurrentEntryFormControl {
  /** return the field's FormControl of the currently opened ShipmentDetail panel */
  control: () => Promise<AbstractControl>,
  /** set focus to the field's FormControl of the currently opened ShipmentDetail panel */
  focus: () => Promise<AbstractControl>,
  /** clear and set focus to the field control */
  reset: () => Promise<AbstractControl>
}

interface IGetItemAndInventoryResult {
  item: StockItem,
  inventory: InventorySource[]
}

// #endregion